import type { SceneNode } from "@/document/model/nodes";
import { normalizeFillStack, normalizeStrokeStack } from "@/document/model/styles";
import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";
import { planarizeVectorNetwork } from "@/document/operations/vectorPlanarization";
import { isClosedVectorNetwork, sampleEdgeBezier, sampleNetworkContours } from "@/document/operations/vectorGeometry";
import { regionKey, vectorRegionGeometries, type VectorRegionGeometry } from "@/document/operations/vectorRegions";
import {
  boundsFromContours,
  contourFormsFragmentHole,
  contourInteriorPoint,
  contourParentsContour,
  contoursIntersect,
  pointFilledInContours,
  pointInPolygon,
  signedArea,
  type Point,
} from "@/document/operations/pathGeometry";
import type { DisplayItem } from "../displayList";
import {
  fillChunkGroupThreshold,
  formatCacheNumber,
  networkMeshCache,
  type CachedPathMesh,
  type FillContourGroup,
} from "./cache";
import {
  getGeometryPool,
  networkOrigin,
  sharedNetworkGeometryKey,
  translateNetworkMesh,
  translateNumberVertices,
  translateVectorNetwork,
  type NetworkMesh,
} from "./geometryPool";
import type { DisplayListOptions } from "./walk";
import {
  boundsFromVertices,
  cachedContourGroupTriangles,
  cachedUnchangedFillChunk,
  paintToDisplayPaint,
  removeDuplicatePoints,
  triangulateEvenOddContours,
  triangulateIndependentContours,
} from "./pathNodes";
import {
  adaptiveCurveStepsForEdge,
  roundVectorPolylineCorners,
  sampleEdgePointsOriented,
  strokeGeometryCacheKey,
} from "./strokeMesh";
import { flattenMeshes, networkStrokeTriangleChunks } from "./meshKernels";

export interface VectorLookup {
  vertexById: Map<string, VectorVertex>;
  incidentEdgesByVertexId: Map<string, VectorEdge[]>;
  vertexByPointKey: Map<string, VectorVertex>;
}

export function vectorLookup(network: VectorNetwork): VectorLookup {
  const vertexById = vertexMap(network.vertices);
  const incidentEdgesByVertexId = new Map<string, VectorEdge[]>();
  for (const edge of network.edges) {
    addIncidentEdge(incidentEdgesByVertexId, edge.from.vertexId, edge);
    addIncidentEdge(incidentEdgesByVertexId, edge.to.vertexId, edge);
  }

  return {
    vertexById,
    incidentEdgesByVertexId,
    vertexByPointKey: new Map(network.vertices.map(vertex => [pointLookupKey(vertex), vertex])),
  };
}

export function vertexMap(vertices: VectorVertex[]): Map<string, VectorVertex> {
  return new Map(vertices.map(vertex => [vertex.id, vertex]));
}

function addIncidentEdge(map: Map<string, VectorEdge[]>, vertexId: string, edge: VectorEdge): void {
  const edges = map.get(vertexId);
  if (edges) {
    edges.push(edge);
  } else {
    map.set(vertexId, [edge]);
  }
}

export function pointLookupKey(point: Point): string {
  return `${formatCacheNumber(point.x)},${formatCacheNumber(point.y)}`;
}

export function nearbyPointLookupKeys(point: Point): string[] {
  const keys: string[] = [];
  for (const dx of [-0.001, 0, 0.001]) {
    for (const dy of [-0.001, 0, 0.001]) {
      keys.push(`${formatCacheNumber(point.x + dx)},${formatCacheNumber(point.y + dy)}`);
    }
  }
  return keys;
}

export function networkToDisplayItems(node: Extract<SceneNode, { type: "vectorNetwork" }>): DisplayItem[] {
  const mesh = cachedNetworkMesh(node);
  const isClosed = mesh.closed;
  const fillVertices = mesh.fillVertices ?? mesh.vertices;
  const shadowContours = mesh.contours.length > 0 ? mesh.contours : undefined;
  const items: DisplayItem[] = isClosed && fillVertices.length > 0
    ? fillItemsForVertexChunks(node, [fillVertices], false, mesh.sharedKey ?? mesh.key, shadowContours)
    : [];

  for (const stroke of normalizeStrokeStack(node.style).filter(stroke => stroke.width > 0)) {
    for (const strokeVertices of cachedNetworkStrokeChunks(node, mesh, stroke.width, stroke.align)) {
      if (strokeVertices.length === 0) {
        continue;
      }

      items.push({
        id: node.id,
        kind: "triangles",
        bounds: node.bounds,
        visualBounds: boundsFromVertices(strokeVertices),
        geometryKey: mesh.sharedKey ?? mesh.key,
        vertices: strokeVertices,
        fill: paintToDisplayPaint(stroke.paint, node.bounds, "#60a5fa"),
      });
    }
  }

  return items;
}

function cachedNetworkStrokeChunks(
  node: Extract<SceneNode, { type: "vectorNetwork" }>,
  mesh: NetworkMesh,
  width: number,
  align: Parameters<typeof strokeGeometryCacheKey>[1],
): Float32Array[] {
  const cacheKey = strokeGeometryCacheKey(width, align);
  const cached = mesh.strokeVerticesByWidth.get(cacheKey);
  if (cached) {
    return cached.length === 0 ? [] : [cached];
  }

  const pool = getGeometryPool();
  // Peek without affecting hit/miss stats by reading the map after an acquire.
  const localMesh = pool.getNetworkMesh(mesh.sharedKey);
  const localCached = localMesh?.strokeVerticesByWidth.get(cacheKey);
  if (localCached) {
    const worldVertices = translateNumberVertices(localCached, mesh.origin.x, mesh.origin.y);
    mesh.strokeVerticesByWidth.set(cacheKey, worldVertices);
    return worldVertices.length === 0 ? [] : [worldVertices];
  }

  const localNetwork = translateVectorNetwork(node.network, -mesh.origin.x, -mesh.origin.y);
  const localChunks = networkStrokeTriangleChunks(localNetwork, width, align);
  const localVertices = flattenMeshes(localChunks);
  localMesh?.strokeVerticesByWidth.set(cacheKey, localVertices);

  const worldChunks = localChunks.map(chunk => translateNumberVertices(chunk, mesh.origin.x, mesh.origin.y));
  mesh.strokeVerticesByWidth.set(cacheKey, flattenMeshes(worldChunks));
  return worldChunks;
}

function fillItemsForVertexChunks(
  node: Extract<SceneNode, { type: "vectorNetwork" }>,
  chunks: Float32Array[],
  includeVisualBounds: boolean,
  geometryKey?: string,
  shadowContours?: Point[][],
): DisplayItem[] {
  const items: DisplayItem[] = [];
  for (const fill of normalizeFillStack(node.style)) {
    for (const vertices of chunks) {
      if (vertices.length === 0) {
        continue;
      }
      items.push({
        id: node.id,
        kind: "triangles" as const,
        bounds: node.bounds,
        visualBounds: includeVisualBounds ? boundsFromVertices(vertices) : undefined,
        geometryKey,
        shadowContours,
        vertices,
        fill: paintToDisplayPaint(fill, node.bounds, "#60a5fa"),
        scaleX: node.transform.scaleX,
        scaleY: node.transform.scaleY,
      });
    }
  }
  return items;
}

export function previewNetworkToDisplayItems(
  node: Extract<SceneNode, { type: "vectorNetwork" }>,
  preview: NonNullable<DisplayListOptions["vectorNetworkPreviews"]> extends Map<string, infer T> ? T : never,
): DisplayItem[] {
  const displayNode = { ...node, bounds: preview.bounds, network: preview.network };
  const changedEdgeIds = previewChangedEdgeIds(node.network, preview.changedVertexIds ?? [], preview.changedEdgeIds ?? []);
  if (changedEdgeIds.size === 0) {
    return networkToDisplayItems(displayNode);
  }

  const baseMesh = cachedNetworkMesh(node);
  if (!baseMesh.closed || !baseMesh.fillGroups || baseMesh.fillGroups.length <= fillChunkGroupThreshold) {
    return networkToDisplayItems(displayNode);
  }

  const previewLookup = vectorLookup(preview.network);
  const previewEdgeById = new Map(preview.network.edges.map(edge => [edge.id, edge]));
  const changedChunks: Float32Array[] = [];
  const unchangedChunks: Float32Array[] = [];
  for (const group of baseMesh.fillGroups) {
    const changed = group.edgeIds.some(edgeId => changedEdgeIds.has(edgeId));
    if (!changed) {
      if (group.vertices.length > 0) {
        unchangedChunks.push(group.vertices);
      }
      continue;
    }

    const contours = group.edgeRefs
      .map(edgeRefs => samplePreviewContour(edgeRefs, preview.network, previewLookup, previewEdgeById))
      .filter(contour => contour.length >= 3);
    if (contours.length === 0) {
      continue;
    }
    const vertices = cachedContourGroupTriangles(contours, preview.network.fillRule);
    if (vertices.length > 0) {
      changedChunks.push(vertices);
    }
  }

  const unchanged = cachedUnchangedFillChunk(baseMesh, changedEdgeIds, unchangedChunks);
  const fillVertexChunks = [...(unchanged.length > 0 ? [unchanged] : []), ...changedChunks];
  const previewMesh = cachedNetworkMesh(displayNode);

  const items = fillItemsForVertexChunks(
    displayNode,
    fillVertexChunks,
    fillVertexChunks.length > 1,
    previewMesh.key,
    previewMesh.contours.length > 0 ? previewMesh.contours : undefined,
  );
  for (const stroke of normalizeStrokeStack(displayNode.style).filter(stroke => stroke.width > 0)) {
    for (const strokeVertices of cachedNetworkStrokeChunks(displayNode, previewMesh, stroke.width, stroke.align)) {
      if (strokeVertices.length === 0) {
        continue;
      }
      items.push({
        id: displayNode.id,
        kind: "triangles",
        bounds: displayNode.bounds,
        visualBounds: boundsFromVertices(strokeVertices),
        geometryKey: previewMesh.sharedKey ?? previewMesh.key,
        vertices: strokeVertices,
        fill: paintToDisplayPaint(stroke.paint, displayNode.bounds, "#60a5fa"),
      });
    }
  }
  return items;
}

export function cachedNetworkMesh(node: Extract<SceneNode, { type: "vectorNetwork" }>): NetworkMesh {
  const network = node.network;
  const sharedKey = sharedNetworkGeometryKey(network);
  const cached = networkMeshCache.get(network) as NetworkMesh | undefined;
  // Content-check complements WeakMap identity (in-place network mutation would otherwise stale).
  if (cached && cached.sharedKey === sharedKey) {
    return cached;
  }

  const origin = networkOrigin(network);
  const pool = getGeometryPool();
  const pooled = pool.getNetworkMesh(sharedKey);
  if (pooled) {
    const worldMesh = translateNetworkMesh(pooled, origin);
    networkMeshCache.set(network, worldMesh);
    return worldMesh;
  }

  const localNetwork = translateVectorNetwork(network, -origin.x, -origin.y);
  const closed = isClosedVectorNetwork(localNetwork);
  const lookup = vectorLookup(localNetwork);
  const fillGroups = closed
    ? fillContourGroupInfos(localNetwork, edge => adaptiveCurveStepsForEdge(lookup.vertexById, edge), lookup)
    : [];
  const contours = fillGroups.flatMap(group => group.contours);
  const fillContourGroups = fillGroups.map(group => group.contours);
  const fillVertexChunks = fillGroups.map(group => group.vertices).filter(vertices => vertices.length > 0);
  const fillVertices = flattenMeshes(fillVertexChunks);
  const localMesh: NetworkMesh = {
    key: sharedKey,
    sharedKey,
    origin: { x: 0, y: 0 },
    closed,
    contours,
    fillContours: fillContourGroups.flat(),
    vertices: new Float32Array(0),
    fillVertices,
    fillVertexChunks,
    fillGroups,
    strokeVerticesByWidth: new Map<string, Float32Array>(),
  };
  pool.setNetworkMesh(sharedKey, localMesh);
  const worldMesh = translateNetworkMesh(localMesh, origin);
  networkMeshCache.set(network, worldMesh);
  return worldMesh;
}

function fillContourGroupInfos(network: VectorNetwork, steps: (edge: VectorEdge) => number, lookup: VectorLookup): FillContourGroup[] {
  const geometries = vectorRegionGeometries(network, steps);
  const childIndexesByParent = directChildRegionIndexes(geometries);
  const fragmentHoleChildrenByParent = fragmentHoleChildrenByParentIndex(geometries);
  const fragmentChildIndexes = new Set<number>(
    [...fragmentHoleChildrenByParent.values()].flatMap(children => children.map(child => geometries.indexOf(child))),
  );

  const sourceGeometries = network.regions.length > 0
    ? geometries.filter(geometry => geometry.region.filled !== false)
    : geometries.filter((_, index) => regionDepth(index, childIndexesByParent) % 2 === 0);

  return sourceGeometries.flatMap(geometry => {
    const geometryIndex = geometries.indexOf(geometry);
    if (fragmentChildIndexes.has(geometryIndex)) {
      return [];
    }

    const nestedChildren = (childIndexesByParent.get(geometryIndex) ?? [])
      .map(index => geometries[index]!)
      .filter(Boolean);
    const fragmentHoleChildren = fragmentHoleChildrenByParent.get(geometryIndex) ?? [];
    const selfIntersects = contourSelfIntersects(geometry.contour);

    const groups: FillContourGroup[] = [];
    if (fragmentHoleChildren.length > 0) {
      groups.push(...triangulateFragmentHoleFillGroup(network, geometry, fragmentHoleChildren, lookup));
    } else if (selfIntersects && network.fillRule === "evenodd") {
      groups.push(...triangulateSelfIntersectingFillGroup(network, geometry, [], lookup));
    } else {
      groups.push(...buildFillContourGroup(geometry, nestedChildren, lookup, network.fillRule));
    }

    return groups;
  });
}

function fragmentHoleChildrenByParentIndex(geometries: VectorRegionGeometry[]): Map<number, VectorRegionGeometry[]> {
  const fragmentHoleChildrenByParent = new Map<number, VectorRegionGeometry[]>();

  for (let parentIndex = 0; parentIndex < geometries.length; parentIndex += 1) {
    const parent = geometries[parentIndex]!;
    const parentArea = Math.abs(signedArea(parent.contour));
    const fragmentHoleChildren: VectorRegionGeometry[] = [];

    for (let childIndex = 0; childIndex < geometries.length; childIndex += 1) {
      if (childIndex === parentIndex) {
        continue;
      }

      const child = geometries[childIndex]!;
      const childArea = Math.abs(signedArea(child.contour));
      if (childArea >= parentArea || !contourFormsFragmentHole(parent.contour, child.contour)) {
        continue;
      }

      fragmentHoleChildren.push(child);
    }

    if (fragmentHoleChildren.length > 0) {
      fragmentHoleChildrenByParent.set(parentIndex, fragmentHoleChildren);
    }
  }

  return fragmentHoleChildrenByParent;
}

function buildFillContourGroup(
  geometry: VectorRegionGeometry,
  childGeometries: VectorRegionGeometry[],
  geometryLookup: VectorLookup,
  fillRule: "nonzero" | "evenodd",
): FillContourGroup[] {
  const edgeRefs = [geometry.edgeRefs, ...childGeometries.map(child => child.edgeRefs)];
  const contours = [geometry.contour, ...childGeometries.map(child => child.contour)]
    .map(contour => roundVectorPolylineCorners(contour, geometryLookup, true))
    .filter(contour => contour.length >= 3);
  if (contours.length === 0) {
    return [];
  }

  const vertices = cachedContourGroupTriangles(contours, fillRule);
  if (vertices.length === 0) {
    return [];
  }

  return [{
    contours,
    edgeRefs,
    edgeIds: [...new Set(edgeRefs.flatMap(refs => refs.map(ref => ref.edgeId)))],
    vertices,
  }];
}

export function triangulateFragmentHoleFillGroup(
  network: VectorNetwork,
  outer: VectorRegionGeometry,
  holes: VectorRegionGeometry[],
  lookup: VectorLookup,
): FillContourGroup[] {
  const sourceContours = [outer.contour, ...holes.map(hole => hole.contour)];
  return triangulatePlanarFillGroup(network, outer, holes, lookup, contour => {
    return pointFilledInContours(contourInteriorPoint(contour), sourceContours, network.fillRule);
  }).map(group => ({
    ...group,
    vertices: filterFillTriangles(group.vertices, sourceContours, network.fillRule),
  })).filter(group => group.vertices.length > 0);
}

function filterFillTriangles(vertices: Float32Array, sourceContours: Point[][], fillRule: "nonzero" | "evenodd"): Float32Array {
  const filtered: number[] = [];
  for (let index = 0; index < vertices.length; index += 6) {
    const x1 = vertices[index]!;
    const y1 = vertices[index + 1]!;
    const x2 = vertices[index + 2]!;
    const y2 = vertices[index + 3]!;
    const x3 = vertices[index + 4]!;
    const y3 = vertices[index + 5]!;
    const centroid = { x: (x1 + x2 + x3) / 3, y: (y1 + y2 + y3) / 3 };
    if (!pointFilledInContours(centroid, sourceContours, fillRule)) {
      continue;
    }

    filtered.push(x1, y1, x2, y2, x3, y3);
  }

  return new Float32Array(filtered);
}

export function triangulateSelfIntersectingFillGroup(
  network: VectorNetwork,
  outer: VectorRegionGeometry,
  children: VectorRegionGeometry[],
  lookup: VectorLookup,
): FillContourGroup[] {
  const sourceContours = [outer.contour, ...children.map(child => child.contour)];
  return triangulatePlanarFillGroup(network, outer, children, lookup, contour => {
    return planarFaceIsFilled(contour, sourceContours, network.fillRule);
  });
}

function planarFaceIsFilled(contour: Point[], sourceContours: Point[][], fillRule: "nonzero" | "evenodd"): boolean {
  const xs = contour.map(point => point.x);
  const ys = contour.map(point => point.y);
  const samples = [
    contourInteriorPoint(contour),
    { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 },
    ...contour,
  ];

  return samples.some(sample => pointFilledInContours(sample, sourceContours, fillRule));
}

export function triangulatePlanarFillGroup(
  network: VectorNetwork,
  outer: VectorRegionGeometry,
  holes: VectorRegionGeometry[],
  lookup: VectorLookup,
  faceIsFilled: (contour: Point[]) => boolean,
): FillContourGroup[] {
  const edgeIds = new Set([
    ...outer.edgeRefs.map(ref => ref.edgeId),
    ...holes.flatMap(hole => hole.edgeRefs.map(ref => ref.edgeId)),
  ]);
  const vertexIds = new Set<string>();
  for (const edge of network.edges) {
    if (!edgeIds.has(edge.id)) {
      continue;
    }

    vertexIds.add(edge.from.vertexId);
    vertexIds.add(edge.to.vertexId);
  }

  const subnetwork: VectorNetwork = {
    vertices: network.vertices.filter(vertex => vertexIds.has(vertex.id)),
    edges: network.edges.filter(edge => edgeIds.has(edge.id)),
    regions: [],
    fillRule: network.fillRule,
    fillOverrideTable: network.fillOverrideTable,
  };
  const planarNetwork = planarizeVectorNetwork(subnetwork);
  const planarLookup = vectorLookup(planarNetwork);
  const planarSteps = (edge: VectorEdge) => adaptiveCurveStepsForEdge(planarLookup.vertexById, edge);
  const planarGeometries = vectorRegionGeometries(planarNetwork, planarSteps);

  return planarGeometries.flatMap(planarGeometry => {
    if (!faceIsFilled(planarGeometry.contour)) {
      return [];
    }

    const contours = [roundVectorPolylineCorners(planarGeometry.contour, planarLookup, true)].filter(contour => contour.length >= 3);
    if (contours.length === 0) {
      return [];
    }

    const vertices = cachedContourGroupTriangles(contours, network.fillRule);
    if (vertices.length === 0) {
      return [];
    }

    return [{
      contours,
      edgeRefs: [planarGeometry.edgeRefs],
      edgeIds: planarGeometry.edgeRefs.map(ref => ref.edgeId),
      vertices,
    }];
  });
}

function contourSelfIntersects(contour: Point[]): boolean {
  const points = removeDuplicatePoints(contour);
  if (points.length < 4) {
    return false;
  }

  for (let firstIndex = 0; firstIndex < points.length; firstIndex += 1) {
    const firstFrom = points[firstIndex]!;
    const firstTo = points[(firstIndex + 1) % points.length]!;
    for (let secondIndex = firstIndex + 1; secondIndex < points.length; secondIndex += 1) {
      if (edgesAreAdjacent(firstIndex, secondIndex, points.length)) {
        continue;
      }

      const secondFrom = points[secondIndex]!;
      const secondTo = points[(secondIndex + 1) % points.length]!;
      if (segmentsProperlyIntersect(firstFrom, firstTo, secondFrom, secondTo)) {
        return true;
      }
    }
  }

  return false;
}

function edgesAreAdjacent(firstIndex: number, secondIndex: number, edgeCount: number): boolean {
  return Math.abs(firstIndex - secondIndex) === 1 || Math.abs(firstIndex - secondIndex) === edgeCount - 1;
}

function segmentsProperlyIntersect(firstFrom: Point, firstTo: Point, secondFrom: Point, secondTo: Point): boolean {
  const firstDx = firstTo.x - firstFrom.x;
  const firstDy = firstTo.y - firstFrom.y;
  const secondDx = secondTo.x - secondFrom.x;
  const secondDy = secondTo.y - secondFrom.y;
  const denominator = cross2d(firstDx, firstDy, secondDx, secondDy);
  if (Math.abs(denominator) <= 1e-9) {
    return false;
  }

  const deltaX = secondFrom.x - firstFrom.x;
  const deltaY = secondFrom.y - firstFrom.y;
  const firstT = cross2d(deltaX, deltaY, secondDx, secondDy) / denominator;
  const secondT = cross2d(deltaX, deltaY, firstDx, firstDy) / denominator;
  return firstT > 0.0001 && firstT < 0.9999 && secondT > 0.0001 && secondT < 0.9999;
}

function cross2d(ax: number, ay: number, bx: number, by: number): number {
  return ax * by - ay * bx;
}

function directChildRegionIndexes(geometries: VectorRegionGeometry[]): Map<number, number[]> {
  const childIndexesByParent = new Map<number, number[]>();
  for (let childIndex = 0; childIndex < geometries.length; childIndex += 1) {
    const child = geometries[childIndex]!;
    const childArea = Math.abs(signedArea(child.contour));
    let parentIndex: number | undefined;
    let parentArea = Number.POSITIVE_INFINITY;

    for (let candidateIndex = 0; candidateIndex < geometries.length; candidateIndex += 1) {
      if (candidateIndex === childIndex) {
        continue;
      }

      const candidate = geometries[candidateIndex]!;
      const candidateArea = Math.abs(signedArea(candidate.contour));
      if (candidateArea <= childArea || candidateArea >= parentArea || !contourParentsContour(candidate.contour, child.contour)) {
        continue;
      }

      parentIndex = candidateIndex;
      parentArea = candidateArea;
    }

    if (parentIndex !== undefined) {
      childIndexesByParent.set(parentIndex, [...(childIndexesByParent.get(parentIndex) ?? []), childIndex]);
    }
  }
  return childIndexesByParent;
}

function regionDepth(index: number, childIndexesByParent: Map<number, number[]>): number {
  let depth = 0;
  let current = index;
  while (true) {
    const parent = Array.from(childIndexesByParent.entries()).find(([, childIndexes]) => childIndexes.includes(current))?.[0];
    if (parent === undefined) {
      return depth;
    }
    depth += 1;
    current = parent;
  }
}

function previewChangedEdgeIds(network: VectorNetwork, changedVertexIds: string[], changedEdgeIds: string[]): Set<string> {
  const result = new Set(changedEdgeIds);
  const vertices = new Set(changedVertexIds);
  if (vertices.size === 0) {
    return result;
  }

  for (const edge of network.edges) {
    if (vertices.has(edge.from.vertexId) || vertices.has(edge.to.vertexId)) {
      result.add(edge.id);
    }
  }
  return result;
}

function samplePreviewContour(
  edgeRefs: Array<{ edgeId: string; forward: boolean }>,
  network: VectorNetwork,
  lookup: VectorLookup,
  edgeById: Map<string, VectorEdge>,
): Point[] {
  const points = edgeRefs.flatMap((ref, index) => {
    const edge = edgeById.get(ref.edgeId);
    if (!edge) {
      return [];
    }
    const sampled = sampleEdgePointsOriented(lookup.vertexById, edge, adaptiveCurveStepsForEdge(lookup.vertexById, edge), ref.forward);
    return index === 0 ? sampled : sampled.slice(1);
  });
  return roundVectorPolylineCorners(points, lookup, true);
}

/**
 * Absolute + ID-dependent network fingerprint. Prefer {@link sharedNetworkGeometryKey}
 * for GeometryPool / content-keyed caches (translation-normalized, ID-independent).
 */
export function networkGeometryKey(network: VectorNetwork): string {
  return [
    network.fillRule,
    ...network.vertices.map(vertex => [
      vertex.id,
      formatCacheNumber(vertex.x),
      formatCacheNumber(vertex.y),
      vertex.handleMode ?? "none",
      vertex.strokeJoin ?? "miter",
      formatCacheNumber(vertex.cornerRadius ?? 0),
    ].join(",")),
    ...network.regions.map(region => [
      region.id,
      regionKey(region.edgeIds),
      region.filled === false ? "0" : "1",
      region.fillStyleId ?? "",
    ].join(",")),
    ...network.edges.map(edge => [
      edge.id,
      edge.from.vertexId,
      edge.from.handle ? `${formatCacheNumber(edge.from.handle.x)},${formatCacheNumber(edge.from.handle.y)}` : "",
      edge.to.vertexId,
      edge.to.handle ? `${formatCacheNumber(edge.to.handle.x)},${formatCacheNumber(edge.to.handle.y)}` : "",
      edge.directed ? "1" : "0",
    ].join(",")),
  ].join("|");
}
